import React, { FC, useEffect, useRef, useState } from 'react';
import { Control, UseFormSetError, UseFormSetValue, useWatch } from 'react-hook-form';

import { CheckIcon, InfoCircle } from '../../../../assets/icons/index';
import {
  AnimatedCalculatedInput,
  AnimatedNumber,
} from '../../../../components/animated-number/index';
import { AppFormattedMessage } from '../../../../components/app-formatted-message/index';
import { FormDatePicker } from '../../../../components/date-picker/index';
import { DropDown } from '../../../../components/dropdown/index';
import {
  HoverCard,
  HoverCardContent,
  HoverCardTrigger,
} from '../../../../components/hover-card/index';
import { FormInput } from '../../../../components/input/index';
import { FormRadio, FormRadioGroup } from '../../../../components/radio-group/index';
import { Tooltip } from '../../../../components/tooltip/index';
import { EventHooks, useDebounce, useFormat } from '../../../../hooks/index';
import { ValuationSelect, ValuationSelectTitle } from '../../../../types/events.types';
import { removeExtraSpaces } from '../../../../utils/string-transformers.util';
import { toMathRound } from '../../../../utils/to-math-round.util';
import { toNumber } from '../../../../utils/to-number.util';
import { FormSchema } from '../event-form.validation';
import { eventStorage } from '../steps/steps.store';
import { deriveSharePriceFromPreMoney } from './valuation-share-price.util';

export type ValuationStepOneProps = {
  companyId: string;
  control: Control<FormSchema>;
  issuedShares: number;
  lockMode?: boolean;
  setError: UseFormSetError<FormSchema>;
  setFormData: UseFormSetValue<FormSchema>;
};

export const ValuationStepOne: FC<ValuationStepOneProps> = ({
  companyId,
  control,
  issuedShares,
  lockMode,
  setError,
  setFormData,
}) => {
  const { stepOne } = useWatch<FormSchema>({ control });
  const { format } = useFormat();
  const startLoading = eventStorage(({ startLoading }) => startLoading);
  const finishAll = eventStorage(({ finishAll }) => finishAll);
  const setValuationNameTaken = eventStorage(({ setValuationNameTaken }) => setValuationNameTaken);
  const event = stepOne?.eventDetails?.valuation;
  const [preMoneyValuation, setPreMoneyValuation] = useState(
    toNumber(stepOne?.eventDetails?.valuation?.sharePrice) *
      toNumber(stepOne?.eventDetails?.valuation?.issuedSharesOnStart),
  );
  const [sharePrice, setSharePrice] = useState(stepOne?.eventDetails?.valuation?.sharePrice);

  const { debounceCallback: debouncePreMoneyValuation, cancelTimeout: cancelPreMoneyValuation } =
    useDebounce({
      callback: (calculatedPreMoneyValuation: number) => {
        setPreMoneyValuation(calculatedPreMoneyValuation);
        setFormData(
          'stepOne.eventDetails.valuation.preMoneyValuation',
          calculatedPreMoneyValuation,
        );
      },
      debounce: 100,
    });

  const { debounceCallback: checkNameDebounce } = useDebounce({
    callback: (name: string) => {
      checkName(name.trim(), {
        onSuccess: ({ isAvailable }) => {
          finishAll();
          setValuationNameTaken(!isAvailable);
          if (isAvailable) return;
          setError('stepOne.eventDetails.valuation.name', {
            message: 'Valuation event with this name already exists',
            type: 'value',
          });
        },
      });
    },
    debounce: 200,
  });

  // The verdict is form-wide state (the step gate reads it), so it must not outlive this form.
  useEffect(() => () => setValuationNameTaken(false), [setValuationNameTaken]);

  const { checkName } = EventHooks.useValuationNameExist({
    companyId,
  });

  const { debounceCallback: debounceSharePrice, cancelTimeout: cancelSharePrice } = useDebounce({
    callback: (calculatedSharePrice: number) => {
      setSharePrice(calculatedSharePrice);
      setFormData('stepOne.eventDetails.valuation.sharePrice', calculatedSharePrice);
    },
  });

  const issuedSharesValue =
    stepOne?.eventDetails?.valuation?.issuedSharesOnStart !== undefined
      ? Number(stepOne?.eventDetails?.valuation?.issuedSharesOnStart)
      : Number(issuedShares);

  // Share price is the entered source in Pre-money mode; do not derive Pre-money back into it,
  // otherwise the two derive-effects clobber the field the user is editing (clears it to 0/empty).
  useEffect(() => {
    if (event?.valuationSelect === ValuationSelect.PRE_MONEY_VALUATION) {
      // Pre-money is the entered source now — drop any pending derive-back write so a stale
      // timer (scheduled before the mode switch) cannot clobber what the user just typed.
      cancelPreMoneyValuation();
      return;
    }

    const calculatedPreMoneyValuation =
      lockMode && event?.sharePrice && event?.issuedSharesOnStart !== undefined
        ? event.sharePrice * event.issuedSharesOnStart
        : event?.sharePrice
          ? event.sharePrice * Number(issuedShares)
          : 0;
    debouncePreMoneyValuation(calculatedPreMoneyValuation);
  }, [
    cancelPreMoneyValuation,
    debouncePreMoneyValuation,
    event?.issuedSharesOnStart,
    event?.sharePrice,
    event?.valuationSelect,
    issuedShares,
    lockMode,
    setFormData,
  ]);

  // Only derive Share price from the entered Pre-money value while Pre-money mode is active.
  useEffect(() => {
    if (event?.valuationSelect !== ValuationSelect.PRE_MONEY_VALUATION) {
      // Share price is entered directly in Share-price mode — cancel any stale derived write.
      cancelSharePrice();
      return;
    }

    debounceSharePrice(
      deriveSharePriceFromPreMoney({
        issuedShares: Number(issuedShares),
        issuedSharesOnStart: event?.issuedSharesOnStart,
        lockMode,
        preMoneyValuation: event?.preMoneyValuation,
      }),
    );
  }, [
    cancelSharePrice,
    debounceSharePrice,
    event?.issuedSharesOnStart,
    event?.preMoneyValuation,
    event?.valuationSelect,
    issuedShares,
    lockMode,
    setFormData,
  ]);

  // On switching to Pre-money mode, immediately commit the value derived from the share
  // price entered in Share-price mode. The guarded derive-effects above are transition-safe
  // by design (they cancel pending writes on a mode switch), so without this one-shot seed
  // the pre-money field and the derived share-price box start at 0 instead of the
  // recalculated values. Runs on the transition only — it cannot clobber live typing.
  const previousValuationSelect = useRef(event?.valuationSelect);
  useEffect(() => {
    const previous = previousValuationSelect.current;
    if (previous === event?.valuationSelect) return;
    previousValuationSelect.current = event?.valuationSelect;
    if (!previous || event?.valuationSelect !== ValuationSelect.PRE_MONEY_VALUATION) return;

    const enteredSharePrice = toNumber(event?.sharePrice);
    const seededPreMoneyValuation = enteredSharePrice * issuedSharesValue;

    cancelPreMoneyValuation();
    setSharePrice(enteredSharePrice);
    setPreMoneyValuation(seededPreMoneyValuation);
    setFormData('stepOne.eventDetails.valuation.preMoneyValuation', seededPreMoneyValuation);
  }, [
    cancelPreMoneyValuation,
    event?.sharePrice,
    event?.valuationSelect,
    issuedSharesValue,
    setFormData,
  ]);

  useEffect(() => {
    if (!Number(issuedShares) && !event?.issuedSharesOnStart)
      return setFormData(
        'stepOne.eventDetails.valuation.valuationSelect',
        ValuationSelect.SHARE_PRICE,
      );

    if (lockMode && event?.valuationSelect === ValuationSelect.PRE_MONEY_VALUATION)
      setFormData(
        'stepOne.eventDetails.valuation.valuationSelect',
        ValuationSelect.PRE_MONEY_VALUATION,
      );
  }, [
    event?.issuedSharesOnStart,
    event?.valuationSelect,
    issuedShares,
    lockMode,
    setFormData,
    sharePrice,
  ]);

  const isRadioDisabled =
    (lockMode && !event?.issuedSharesOnStart) || (!lockMode && !Number(issuedShares));

  const valuationSelect = stepOne?.eventDetails?.valuation?.valuationSelect;

  return (
    <div className="flex w-full flex-col gap-8">
      <div className="flex w-full flex-col gap-4">
        <FormInput
          control={control}
          name={'stepOne.eventDetails.valuation.name'}
          onChange={(value) => {
            startLoading();
            // Clear the previous verdict immediately: it belongs to the name the user just left.
            setValuationNameTaken(false);
            checkNameDebounce(value);
          }}
          placeholder={<AppFormattedMessage id="EVENT_NAME" />}
          regexp={removeExtraSpaces}
          wrapperClassName="w-full"
        />
        <FormDatePicker
          iconColor="#98A2B3"
          inputValue={
            stepOne?.eventDetails?.valuation?.date
              ? format(stepOne?.eventDetails?.valuation?.date, 'dd/MM/yyyy')
              : ''
          }
          inputWrapperClassName="w-full"
          onSelect={(date) => {
            setFormData('stepOne.eventDetails.valuation.date', date);
          }}
          placeholder="Event Date"
          value={stepOne?.eventDetails?.valuation?.date}
        />
      </div>
      <FormRadioGroup
        className={'flex shrink-0 gap-4 divide-x-0 overflow-x-auto'}
        control={control}
        name="stepOne.eventDetails.valuation.valuationSelect"
      >
        {Object.entries(ValuationSelectTitle).map(([valuationKey, valuationTitle]) => (
          <div className="flex items-center gap-2" key={valuationKey}>
            {isRadioDisabled &&
            stepOne?.eventDetails?.valuation?.valuationSelect !== valuationKey ? (
              <>
                <FormRadio
                  className={
                    'flex size-4 items-center justify-center overflow-hidden rounded-full border border-gray-300 data-[checked]:border-none data-[checked]:bg-brand-500'
                  }
                  control={control}
                  disabled={isRadioDisabled}
                  name="stepOne.eventDetails.valuation.valuationSelect"
                  styleType="NONE"
                  value={valuationKey}
                >
                  <CheckIcon
                    className="hidden size-[6.5px] group-data-[checked]:block"
                    iconColor={'#ffffff'}
                  />
                </FormRadio>

                <Tooltip content="Pre-money Valuation method can't be set with 0 issued shares. Enter Share Price to proceed.">
                  <span className="text-nowrap text-sm font-[450] text-gray-400">
                    <AppFormattedMessage id={valuationTitle} />
                  </span>
                </Tooltip>
              </>
            ) : (
              <>
                <FormRadio
                  className={
                    'flex size-4 cursor-pointer items-center justify-center overflow-hidden rounded-full border border-gray-300 data-[checked]:border-none data-[checked]:bg-brand-500'
                  }
                  control={control}
                  disabled={isRadioDisabled}
                  name="stepOne.eventDetails.valuation.valuationSelect"
                  styleType="NONE"
                  value={valuationKey}
                >
                  <CheckIcon
                    className="hidden size-[6.5px] group-data-[checked]:block"
                    iconColor={'#ffffff'}
                  />
                </FormRadio>
                <span className="cursor-default text-nowrap text-sm font-[450] text-gray-700">
                  <AppFormattedMessage id={valuationTitle} />
                </span>
              </>
            )}
          </div>
        ))}
      </FormRadioGroup>
      {valuationSelect === ValuationSelect.SHARE_PRICE && (
        <div className="flex flex-col gap-6">
          <FormInput
            control={control}
            currencySign
            maxDecimalPlaces={4}
            name={'stepOne.eventDetails.valuation.sharePrice'}
            placeholder={<AppFormattedMessage id="SHARES_PRICE" />}
            shouldDisplayZero
            shouldFormatNumber
            transform={(value) => {
              return value !== undefined ? toMathRound(value, 4).toString() : '';
            }}
            wrapperClassName="w-full"
          />
          <div className="flex flex-col gap-3 rounded-md bg-brand-25 px-1 pb-1 pt-3">
            <div className="flex flex-col items-start gap-1 pl-[14px]">
              <div className="flex items-center gap-2">
                <span className="text-sm font-[550] text-brand-700">
                  <AppFormattedMessage id="PRE_MONEY_VALUATION" />
                </span>
                <HoverCard>
                  <HoverCardTrigger>
                    <InfoCircle className="size-4 cursor-pointer" iconColor="#2565C8 " />
                  </HoverCardTrigger>
                  <HoverCardContent
                    className="z-20 w-max rounded-lg bg-[#101828] px-3 pb-[6px] pt-1"
                    isShownTriangle
                    side="top"
                    triangleClassName="bottom-0 left-1/2 transform -translate-x-1/2 translate-y-1/2 ml-0"
                  >
                    <span className="text-label-md font-[550] text-white">
                      <AppFormattedMessage id="CALCULATED_BY" />
                    </span>
                  </HoverCardContent>
                </HoverCard>
              </div>
              <span className="text-xs font-[450] text-gray-700">
                <AppFormattedMessage id="ISSUED_SHARES" />:{' '}
                <AnimatedNumber integer value={issuedSharesValue ?? 0} />
              </span>
            </div>
            <div className="flex flex-col gap-3 rounded-md bg-white p-3 shadow-xs">
              <AnimatedCalculatedInput
                currencySign
                placeholder={<AppFormattedMessage id="VALUE" />}
                value={preMoneyValuation}
                wrapperClassName="w-full"
              />
              <DropDown
                answer="The Pre-Money Valuation is determined by multiplying the share price entered with the total number of issued shares at the time of the event. This value reflects the estimated value of the company before any new funding or investment is taken into account."
                className="rounded-lg bg-gray-50"
                question={<AppFormattedMessage id="VALUE_CALCULATION_INFO" />}
              />
            </div>
          </div>
        </div>
      )}
      {valuationSelect === ValuationSelect.PRE_MONEY_VALUATION && (
        <div className="flex flex-col gap-6">
          <FormInput
            control={control}
            currencySign
            name={'stepOne.eventDetails.valuation.preMoneyValuation'}
            placeholder={<AppFormattedMessage id="PRE_MONEY_VALUATION" />}
            shouldDisplayZero
            shouldFormatNumber
            transform={(value) => {
              return value !== undefined ? toMathRound(value, 4).toString() : '';
            }}
            wrapperClassName="w-full"
          />
          <div className="flex flex-col gap-3 rounded-md bg-brand-25 px-1 pb-1 pt-3">
            <div className="flex flex-col items-start gap-1 pl-[14px]">
              <div className="flex items-center gap-2">
                <span className="text-sm font-[550] text-brand-700">
                  <AppFormattedMessage id="SHARES_PRICE" />
                </span>
                <HoverCard>
                  <HoverCardTrigger>
                    <InfoCircle className="size-4 cursor-pointer" iconColor="#2565C8 " />
                  </HoverCardTrigger>
                  <HoverCardContent
                    className="z-20 w-max rounded-lg bg-[#101828] px-3 pb-[6px] pt-1"
                    isShownTriangle
                    side="top"
                    triangleClassName="bottom-0 left-1/2 transform -translate-x-1/2 translate-y-1/2 ml-0"
                  >
                    <span className="text-label-md font-[550] text-white">
                      <AppFormattedMessage id="CALCULATED_BY" />
                    </span>
                  </HoverCardContent>
                </HoverCard>
              </div>
              <span className="text-xs font-[450] text-gray-700">
                <AppFormattedMessage id="ISSUED_SHARES" />:{' '}
                <AnimatedNumber integer value={issuedSharesValue ?? 0} />
              </span>
            </div>
            <div className="flex flex-col gap-3 rounded-md bg-white p-3 shadow-xs">
              <AnimatedCalculatedInput
                currencySign
                maxDecimalPlaces={4}
                placeholder={<AppFormattedMessage id="VALUE" />}
                value={toMathRound(toNumber(sharePrice), 4)}
                wrapperClassName="w-full"
              />
              <DropDown
                answer="The Share Price is determined by dividing the Pre-Money Valuation by the total number of issued shares at the time of the event. This value reflects the price of a single share of the company before any new funding or investment is taken into account."
                className="rounded-lg bg-gray-50"
                question={<AppFormattedMessage id="VALUE_CALCULATION_INFO" />}
              />
            </div>
          </div>
        </div>
      )}
      <DropDown
        answer={[
          'A Valuation Event marks a significant moment in a company’s history that impacts its valuation. This typically includes key milestones such as the Company Foundation, Seed Round, Series A, and other major events.',
          'Valuation Events are recorded and serve as reference points when structuring subsequent events like Share Issuances and Fundraising Rounds.',
          'Each Valuation Event creates a point in the company’s Valuation History, helping track its financial progress over time.',
        ]}
        className="rounded-lg bg-gray-50"
        question={<AppFormattedMessage id="WHAT_IS_VALUATION" />}
      />
    </div>
  );
};
